import { AgentDuplicateInsights } from '../../types/AgentDuplicates';
import { ChatEngine } from './agentEngine';

export type AgentRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type AgentMessageRole = 'user' | 'agent' | 'thinking' | 'tool';

/** A screenshot sent with a question, kept as base64 so history re-renders it. */
export interface AgentChatImageAttachment {
  id: string;
  mediaType: string;
  /** Base64 image bytes without the data: URL prefix. */
  data: string;
}

export interface AgentChatMessage {
  id: string;
  role: AgentMessageRole;
  content: string;
  createdAt: string;
  attachments?: AgentChatImageAttachment[] | null;
  duplicateInsights?: AgentDuplicateInsights | null;
}

/**
 * Token totals reported by the agent, summed over every turn. The SDK exposes
 * no price and no context window size, so there is deliberately no cost or
 * "context remaining" field here rather than an estimated one.
 */
export interface AgentRunUsage {
  turns: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

/** One conversation, which may hold many question-and-answer turns. */
export interface AgentRun {
  id: string;
  /** First question asked, used as the conversation title. */
  prompt: string;
  status: AgentRunStatus;
  createdAt: string;
  messages: AgentChatMessage[];
  /** Cursor agent id, present once the run has started. */
  agentId?: string | null;
  error?: string | null;
  usage?: AgentRunUsage | null;
  /** Real billed cost in cents, once Cursor usage has been reconciled. */
  costCents?: number | null;
  /** Model the conversation is running on. */
  model?: string | null;
  /** Variant params for that model, e.g. effort or reasoning level. */
  modelParams?: AgentModelParam[] | null;
  /** cursor = Cursor SDK. direct = Claude with the same DJ tools. */
  engine?: ChatEngine | null;
}

export interface AgentModelParam {
  id: string;
  value: string;
}

/** A tunable the model accepts, e.g. effort, reasoning, context or fast. */
export interface AgentModelParameter {
  id: string;
  displayName: string;
  values: Array<{ value: string; displayName: string }>;
}

/** A model the signed-in Cursor account can run. */
export interface AgentModel {
  id: string;
  displayName: string;
  description?: string;
  parameters?: AgentModelParameter[];
  defaultParams?: AgentModelParam[];
}

/** A model plus the parameter values to run it with. */
export interface AgentModelSelection {
  id: string;
  params?: AgentModelParam[];
}

/** A saved instruction this DJ can re-run on demand. */
export interface AgentAutomation {
  id: number;
  name: string;
  instructions: string;
  model?: string | null;
  modelParams?: AgentModelParam[] | null;
  runCount: number;
  lastRunAt?: string | null;
  lastRunId?: string | null;
  lastStatus?: AgentRunStatus | null;
  createdAt: string;
}

export interface AgentPromptSuggestion {
  label: string;
  prompt: string;
}

/**
 * Cheapest Cursor-native model. Locked while the model picker is hidden
 * (CRA-148). Bring the picker back when we want DJs choosing models again.
 */
export const AGENT_DEFAULT_MODEL = 'composer-2.5';

/** Starting points offered in the composer when no run is open. */
export const AGENT_PROMPT_SUGGESTIONS: AgentPromptSuggestion[] = [
  {
    label: 'Find duplicates',
    prompt:
      'Find duplicate tracks in my library and tell me which copy to keep for each group.',
  },
  {
    label: 'Library overview',
    prompt:
      'Give me an overview of my library — how big it is, my top artists and genres, and anything that looks off.',
  },
  {
    label: 'Build a crate',
    prompt:
      'Build a peak-time house crate from my library and export it to Serato.',
  },
  {
    label: 'Audit my crates',
    prompt:
      'Look at my Crate Hackers crates and tell me which ones are nearly empty or overlap heavily.',
  },
  {
    label: 'Find tracks',
    prompt:
      'Find every track in my library between 120 and 126 BPM and group them by key.',
  },
];

export const AGENT_RUN_STATUS_LABELS: Record<AgentRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const AGENT_RUN_STATUS_COLORS: Record<AgentRunStatus, string> = {
  queued: 'medium',
  running: 'primary',
  completed: 'success',
  failed: 'danger',
  cancelled: 'medium',
};
